import { computed, Injectable, signal } from '@angular/core';

import { FlowchartExportUtil } from '../infrastructure/operations/flowchart-export.util';

@Injectable({ providedIn: 'root' })
export class FlowchartImageCacheService {
  private cache = signal<Map<string, Blob>>(new Map());
  private cachedIds = computed(() => new Set(this.cache().keys()));

  private expectedWidgetIds = signal(new Set<string>());

  private pendingCaptures = new Set<string>();
  private renderQueue: Promise<void> = Promise.resolve();
  private generation = 0;
  private readonly maxIdleWaitFrames = 120;

  public isReady = computed(() => {
    const expected = this.expectedWidgetIds();
    if (expected.size === 0) {
      return true;
    }

    const cached = this.cachedIds();

    for (const id of expected) {
      if (!cached.has(id)) {
        return false;
      }
    }

    return true;
  });

  public setExpectedWidgetIds(widgetIds: string[]): void {
    this.expectedWidgetIds.set(new Set(widgetIds));
  }

  public renderFlowchart(widgetId: string, paper: joint.dia.Paper): void {
    if (!this.expectedWidgetIds().has(widgetId) || this.cache().has(widgetId) || this.pendingCaptures.has(widgetId)) {
      return;
    }
    this.pendingCaptures.add(widgetId);
    const generation = this.generation;
    this.renderQueue = this.renderQueue.then(() => this.captureWhenIdle(widgetId, paper, generation));
  }

  private captureWhenIdle(widgetId: string, paper: joint.dia.Paper, generation: number): Promise<void> {
    if (generation !== this.generation || this.cache().has(widgetId)) {
      this.pendingCaptures.delete(widgetId);
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      const paperWithIdle = paper as joint.dia.Paper & { isIdle?: () => boolean };
      let framesWaited = 0;

      const waitForIdle = () => {
        if (generation !== this.generation) {
          this.pendingCaptures.delete(widgetId);
          resolve();
          return;
        }

        const isIdle = typeof paperWithIdle.isIdle !== 'function' || paperWithIdle.isIdle();
        if (isIdle || framesWaited >= this.maxIdleWaitFrames) {
          requestAnimationFrame(() => {
            this.captureImage(widgetId, paper, generation, resolve);
          });
          return;
        }

        framesWaited++;
        requestAnimationFrame(waitForIdle);
      };

      requestAnimationFrame(waitForIdle);
    });
  }

  public getMultiple(widgetIds: string[]): Map<string, Blob> {
    const result = new Map<string, Blob>();
    const cacheMap = this.cache();
    for (const id of widgetIds) {
      const blob = cacheMap.get(id);
      if (blob) {
        result.set(id, blob);
      }
    }
    return result;
  }

  public clearAll(): void {
    this.generation++;
    this.cache.set(new Map());
    this.expectedWidgetIds.set(new Set());
    this.pendingCaptures.clear();
    this.renderQueue = Promise.resolve();
  }

  private captureImage(widgetId: string, paper: joint.dia.Paper, generation: number, done: () => void): void {
    if (generation !== this.generation) {
      done();
      return;
    }

    try {
      FlowchartExportUtil.convertPaperToPngBlob(paper, (blob) => {
        if (blob && generation === this.generation && this.expectedWidgetIds().has(widgetId)) {
          this.cache.update((m) => {
            const next = new Map(m);
            next.set(widgetId, blob);
            return next;
          });
        }
        this.pendingCaptures.delete(widgetId);
        done();
      });
    } catch (error) {
      console.error(`[FlowchartCache] Failed to cache flowchart ${widgetId}:`, error);
      this.pendingCaptures.delete(widgetId);
      done();
    }
  }
}
